import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import styled from 'styled-components';

import { SignUpBlockData } from 'api/cms';
import { CurrentUser } from 'api/currentUser';
import { PublicPage } from 'api/website';
import Box from 'components/Box';
import { RichText } from 'components/Text';
import EmailSignupFormContainer from 'containers/EmailSignupFormContainer';
import { breakpoints, gutterWidth, maxWidth, textStyles } from 'theme';
import { trackEvent } from 'utils/analytics';
import { getImageSrc } from 'utils/getImageSrc';
import { hiDPI } from 'utils/style';
import { getFirstParam } from 'utils/url';
import useTemporaryStorage from 'utils/useTemporaryStorage';

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/jsx-props-no-spreading
const Container = styled(({ imageHandle, ...props }) => <Box {...props} />)`
  background-size: cover;
  background-position: center;
  ${({ imageHandle, mimetype }) => `
    background-image: url('${getImageSrc({
      handle: imageHandle,
      mimetype,
      width: 800,
    })}');
    ${hiDPI()}: {
      background-image: url('${getImageSrc({
        handle: imageHandle,
        mimetype,
        width: 1600,
      })}');
    }
    @media (min-width: ${breakpoints[3]}) {
      background-image: url('${getImageSrc({
        handle: imageHandle,
        mimetype,
        width: 1280,
      })}');
      ${hiDPI()}: {
        background-image: url('${getImageSrc({
          handle: imageHandle,
          mimetype,
          width: 2560,
        })}');
      }
    }
  `}
`;
const Headline = styled(
  ({ text, isPageHeader }: { text: string; isPageHeader: boolean }) => (
    <RichText
      dangerouslySetInnerHTML={{
        __html: `<${isPageHeader ? 'h1' : 'h2'}>${
          text ? text.replace(/<(\/?)(p|h1|h2)>/g, '<$1span>') : ''
        }</${isPageHeader ? 'h1' : 'h2'}>`,
      }}
    />
  )
)`
  && {
    h1,
    h2 {
      ${textStyles.h2};
    }
  }
`;

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
const SignUpBlock = ({
  data: { headline, redirectUrl, bgImage },
  isPageHeader,
  currentUser,
}: {
  data: SignUpBlockData;
  isPageHeader: boolean;
  currentUser: CurrentUser;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line react/no-unused-prop-types
  content: PublicPage;
}) => {
  const location = useLocation();
  const tmpId = getFirstParam(location, 'tmpId');
  const [saveTemporaryStorage] = useTemporaryStorage<{
    redirectUrl: string;
  }>(currentUser.id && currentUser.emailVerified ? tmpId : '');
  const [redirectTo, setRedirectTo] = useState(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    trackEvent('View Sign Up Form');
  }, []);
  useEffect(() => {
    if (!redirectTo && !tmpId && !saved) {
      setSaved(true);
      saveTemporaryStorage({
        SIGNUP_BLOCK_REDIRECT: {
          redirectUrl,
        },
      }).then((id) => {
        setRedirectTo(
          `${window.location.pathname}${window.location.search}${
            window.location.search ? '&' : '?'
          }tmpId=${id}`
        );
      });
    }
  }, [redirectTo, redirectUrl, saveTemporaryStorage, tmpId, saved]);
  return (
    <Container data-qa-id="sign-up">
      <Container
        imageHandle={bgImage ? bgImage.handle : null}
        mimetype={bgImage ? bgImage.mimetype : null}
      >
        <Box
          mx="auto"
          p={[0, null, gutterWidth / 2, gutterWidth]}
          maxWidth={maxWidth[3]}
          width={1}
        >
          <Box display="flex" justifyContent="flex-end">
            <Box
              width={[1, null, 0.5]}
              p={[gutterWidth / 2, null, null, gutterWidth]}
              borderRadius={[0, 0, 12]}
              bg="white"
              boxShadow="0 0 6px rgba(0,0,0,0.3)"
            >
              <Headline text={headline} isPageHeader={isPageHeader} />

              <Box mt="32px">
                <EmailSignupFormContainer
                  isModal={false}
                  redirectTo={redirectTo}
                  savedListing={null}
                  subscribedSubsite={null}
                  onLogin={null}
                  onSignup={null}
                  compactView
                  hideLoginBlock
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Container>
    </Container>
  );
};

export default SignUpBlock;
